import { css } from '@emotion/react';
import { useState } from 'react';
import Paper from '@mui/material/Paper';
import Typography from '@mui/material/Typography';
import LinearProgress from '@mui/material/LinearProgress';
import FileTable from './FileTable';

import useAutofetchedRunFilesAsync from 'store/useAutofetchedRunFilesAsync';
import getProjectId from 'utils/url/getProjectId';

export default function Files(props: { runIds: string[] }) {
  return (
    <section
      css={css`
        flex-grow: 1;
        display: flex;
        flex-direction: column;

        > * {
          flex-grow: 1;
        }
      `}
    >
      <Paper elevation={1} style={{ padding: 48 }}>
        <Typography component="h1" variant="h6">
          Files
        </Typography>
        <br />
        {(() => {
          switch (props.runIds.length) {
            case 0:
              return <div>Select workflow to list files.</div>;
            case 1:
              return <FilesAsync key={props.runIds[0]} runId={props.runIds[0]} />;
            default:
              return <div>Multiple workflows selected. Select one to list files.</div>;
          }
        })()}
      </Paper>
    </section>
  );
}

function FilesAsync(props: { runId: string }) {
  const filesAsync = useAutofetchedRunFilesAsync(props.runId);
  const projectId = getProjectId();
  // Capture the first total we see so "No files" stays sticky once the run
  // has truly no files (later filters that drop count to 0 shouldn't trigger
  // it). Adjusting state during render per the React docs' previous-prop
  // pattern — fires once per run. Use the just-seen total in this same render
  // so we don't flash the FileTable for one frame before showing "No files".
  const [unfilteredTotal, setUnfilteredTotal] = useState<number | null>(null);
  const effectiveTotal =
    unfilteredTotal ??
    (filesAsync.status === 'SUCCESS' || filesAsync.status === 'REFRESH' ? filesAsync.data.total : null);
  if (unfilteredTotal === null && effectiveTotal !== null) {
    setUnfilteredTotal(effectiveTotal);
  }

  switch (filesAsync.status) {
    case 'INIT':
    case 'PENDING':
      return (
        <div>
          <LinearProgress />
        </div>
      );
    case 'ERROR':
      return <div>Something went wrong</div>;
    case 'SUCCESS':
    case 'REFRESH': {
      if (effectiveTotal === 0) {
        return <div>No files</div>;
      }
      return <FileTable key={props.runId} data={filesAsync.data} runId={props.runId} projectId={projectId} />;
    }
  }
}
